import { assertCurrencyCode, minorUnitDigits, minorUnitScale, type CurrencyCode } from "./currency";
import { DEFAULT_ROUNDING, divideRounded, type RoundingMode } from "./rounding";

/**
 * An amount of money: integer minor units (`bigint`, never a float) + ISO currency.
 * 12.50 SAR = `{ amountMinor: 1250n, currency: "SAR" }`; 1.500 BHD = `{ 1500n, "BHD" }`.
 * Values are immutable; every operation returns a new object.
 */
export interface Money {
  readonly amountMinor: bigint;
  readonly currency: CurrencyCode;
}

/** JSON wire form: minor units as a decimal integer string (bigint-safe, no float). */
export interface MoneyJson {
  readonly amountMinor: string;
  readonly currency: CurrencyCode;
}

export class CurrencyMismatchError extends TypeError {
  constructor(a: CurrencyCode, b: CurrencyCode) {
    super(`currency mismatch: ${a} vs ${b}`);
    this.name = "CurrencyMismatchError";
  }
}

export class InvalidAmountError extends TypeError {
  constructor(message = "amount must be an integer number of minor units") {
    super(message);
    this.name = "InvalidAmountError";
  }
}

const INTEGER_STRING = /^-?(0|[1-9]\d*)$/;

/**
 * Parses an integer minor-unit amount. `number` input must be a safe integer (so a float or
 * an imprecise value is rejected, never silently rounded); `string` input must be a plain
 * decimal integer ("1250", "-5").
 */
export function toMinorUnits(amount: bigint | number | string): bigint {
  if (typeof amount === "bigint") return amount;
  if (typeof amount === "number") {
    if (!Number.isSafeInteger(amount)) throw new InvalidAmountError();
    return BigInt(amount);
  }
  if (typeof amount === "string" && INTEGER_STRING.test(amount)) return BigInt(amount);
  throw new InvalidAmountError();
}

/** Creates a `Money`. Throws on a non-integer amount or an unsupported currency. */
export function money(amountMinor: bigint | number | string, currency: CurrencyCode): Money {
  return Object.freeze({
    amountMinor: toMinorUnits(amountMinor),
    currency: assertCurrencyCode(currency),
  });
}

export function zero(currency: CurrencyCode): Money {
  return money(0n, currency);
}

function sameCurrency(a: Money, b: Money): CurrencyCode {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
  return a.currency;
}

export function add(a: Money, b: Money): Money {
  return money(a.amountMinor + b.amountMinor, sameCurrency(a, b));
}

export function subtract(a: Money, b: Money): Money {
  return money(a.amountMinor - b.amountMinor, sameCurrency(a, b));
}

export function negate(a: Money): Money {
  return money(-a.amountMinor, a.currency);
}

export function abs(a: Money): Money {
  return a.amountMinor < 0n ? negate(a) : a;
}

/** Sum of `items` (all in `currency`); an empty list is zero. */
export function sum(currency: CurrencyCode, items: readonly Money[]): Money {
  let total = 0n;
  for (const item of items) {
    if (item.currency !== currency) throw new CurrencyMismatchError(currency, item.currency);
    total += item.amountMinor;
  }
  return money(total, currency);
}

/** -1, 0 or 1. Throws on a currency mismatch (amounts in different currencies never compare). */
export function compare(a: Money, b: Money): -1 | 0 | 1 {
  sameCurrency(a, b);
  if (a.amountMinor === b.amountMinor) return 0;
  return a.amountMinor < b.amountMinor ? -1 : 1;
}

export function equals(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.amountMinor === b.amountMinor;
}

export const isZero = (a: Money): boolean => a.amountMinor === 0n;
export const isPositive = (a: Money): boolean => a.amountMinor > 0n;
export const isNegative = (a: Money): boolean => a.amountMinor < 0n;

/** `a × quantity` for an integer quantity (unit price × count). */
export function multiply(a: Money, quantity: bigint | number): Money {
  return money(a.amountMinor * toMinorUnits(quantity), a.currency);
}

/**
 * `a × numerator / denominator`, rounded once at the end with `rounding` (default half-up).
 * The exact product is formed first, so no precision is lost before the single rounding.
 */
export function multiplyByRatio(
  a: Money,
  numerator: bigint | number,
  denominator: bigint | number,
  rounding: RoundingMode = DEFAULT_ROUNDING,
): Money {
  const n = toMinorUnits(numerator);
  const d = toMinorUnits(denominator);
  return money(divideRounded(a.amountMinor * n, d, rounding), a.currency);
}

/** Basis points: 1 bp = 0.01 %, 10000 bp = 100 %. 15 % = 1500. */
export const BASIS_POINTS_PER_UNIT = 10_000n;

/** `a × basisPoints / 10000`, rounded once (default half-up). */
export function percentageOf(
  a: Money,
  basisPoints: bigint | number,
  rounding: RoundingMode = DEFAULT_ROUNDING,
): Money {
  return multiplyByRatio(a, basisPoints, BASIS_POINTS_PER_UNIT, rounding);
}

/**
 * Splits `a` into parts proportional to non-negative integer `weights` without losing or
 * inventing a minor unit: the parts always sum to `a` exactly (largest-remainder method,
 * ties to the earlier part). Used e.g. to spread a discount or VAT over invoice lines.
 */
export function allocate(a: Money, weights: readonly (bigint | number)[]): Money[] {
  if (weights.length === 0) throw new RangeError("allocate needs at least one weight");
  const w = weights.map((x) => {
    const v = toMinorUnits(x);
    if (v < 0n) throw new RangeError("weights must be non-negative");
    return v;
  });
  const total = w.reduce((s, v) => s + v, 0n);
  if (total === 0n) throw new RangeError("weights must not all be zero");
  const negative = a.amountMinor < 0n;
  const amount = negative ? -a.amountMinor : a.amountMinor;
  const shares = w.map((v) => (amount * v) / total);
  const remainders = w.map((v, i) => ({ i, r: amount * v - shares[i]! * total }));
  let left = amount - shares.reduce((s, v) => s + v, 0n);
  remainders.sort((x, y) => (x.r === y.r ? x.i - y.i : x.r > y.r ? -1 : 1));
  for (const { i } of remainders) {
    if (left === 0n) break;
    shares[i] = shares[i]! + 1n;
    left -= 1n;
  }
  return shares.map((s) => money(negative ? -s : s, a.currency));
}

// ---- Decimal (major-unit) strings ----------------------------------------------------------

/** "12.50" for 1250 SAR, "-1.500" for -1500 BHD — always the currency's full digits. */
export function toDecimalString(a: Money): string {
  const digits = minorUnitDigits(a.currency);
  const scale = minorUnitScale(a.currency);
  const negative = a.amountMinor < 0n;
  const abs = negative ? -a.amountMinor : a.amountMinor;
  const major = abs / scale;
  const minor = (abs % scale).toString().padStart(digits, "0");
  return `${negative ? "-" : ""}${major.toString()}.${minor}`;
}

/**
 * Parses a major-unit decimal string ("12", "12.5", "12.50", "-0.125" for BHD) into exact
 * minor units. ASCII digits only (convert Arabic-Indic digits with `toLatinDigits` first);
 * more fraction digits than the currency allows is an error, never a silent rounding.
 */
export function parseDecimal(input: string, currency: CurrencyCode): Money {
  const digits = minorUnitDigits(currency);
  const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(input.trim());
  if (!match) throw new InvalidAmountError("amount must be a decimal number like 12.50");
  const [, sign, whole, fraction = ""] = match;
  if (fraction.length > digits) {
    throw new InvalidAmountError(`${currency} allows at most ${digits} decimal places`);
  }
  const minor = BigInt(whole!) * minorUnitScale(currency) + BigInt(fraction.padEnd(digits, "0"));
  return money(sign ? -minor : minor, currency);
}

// ---- JSON --------------------------------------------------------------------------------

export function toJson(a: Money): MoneyJson {
  return { amountMinor: a.amountMinor.toString(), currency: a.currency };
}

export function fromJson(value: { amountMinor: unknown; currency: unknown }): Money {
  const amount = value.amountMinor;
  if (typeof amount !== "string" && typeof amount !== "number" && typeof amount !== "bigint") {
    throw new InvalidAmountError();
  }
  return money(amount, assertCurrencyCode(value.currency));
}

/**
 * Converts bigint minor units to a JS `number` for a consumer that cannot take a bigint
 * (gateway SDKs, approval thresholds). Throws `RangeError` beyond `Number.MAX_SAFE_INTEGER`
 * instead of silently losing precision.
 */
export function toSafeNumber(minor: bigint): number {
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new RangeError(`minor-unit amount ${minor} exceeds the safe integer range`);
  }
  return Number(minor);
}
